import { observer } from 'mobx-react-lite';

import { useSetup } from '@/common/utils/hooks';
import {
  absolute,
  absoluteFromDirections,
  Card,
  colors,
  FlexColumn,
  FlexRow,
  fullSize,
  fullWidth,
  growShrink,
  padding,
  relative,
  Splash,
  StrokeText,
} from '@toilet-paper-games/ui';

import { GameInfo } from '@/types';
import { useState } from 'react';
import { ScanCard } from '../components/qr/ScanCard';
import { useHosterAppModel } from '../models/HosterAppModel';

import tpGameBannerPng from '#/tp-games-logo.png';
import { BounceButton } from '@/common/components/base/BounceButton';
import { idPrefix } from '@/common/models/NetworkHandler';
import { URLs } from '@/common/utils/url';
import { Fullscreen, Minimize, Settings } from 'lucide-react';
import { HosterGamePlayer } from '../components/HosterGamePlayer';
import { HosterStatusMessage } from '../components/HosterStatusMessage';

export const useGame = (gameUniqueName: string) => {
  const [game, setGame] = useState<GameInfo | null>(null);

  const appStore = useHosterAppModel();

  useSetup(() => {
    appStore.getGame(gameUniqueName).then((fetchedGame) => {
      setGame(fetchedGame ?? null);
    });
  });

  return game;
};

export const LobbyPage = observer(() => {
  const appStore = useHosterAppModel();
  const networkHandler = appStore.networkHandler;

  if (!networkHandler.networkReady) {
    return (
      <Splash>
        <Card css={[growShrink('2s')]}>Loading...</Card>
      </Splash>
    );
  }

  const joinId = (networkHandler.localConnection.id as string | undefined)?.substring(
    idPrefix.length,
  );

  return (
    <Splash>
      <FlexColumn css={[fullSize]} center>
        <FlexRow
          justifyContent="space-between"
          alignItems="center"
          css={[fullWidth, absolute(0)]}
        >
          <div>
            <a css={{}} href={URLs.blog} target="_blank">
              <img src={tpGameBannerPng} height={50} />
            </a>
          </div>

          <div>
            <FlexRow alignItems="center" css={[{ color: 'white', paddingRight: 8 }]}>
              <BounceButton
                onClick={appStore.uiModel.toggleSettingsModal}
                css={[
                  {
                    padding: 5,
                  },
                ]}
              >
                <Settings />
              </BounceButton>
              <BounceButton
                onClick={appStore.toggleFullscreen}
                css={[
                  {
                    padding: 5,
                  },
                ]}
              >
                {appStore.isFullscreen ? <Minimize /> : <Fullscreen />}
              </BounceButton>
            </FlexRow>
          </div>
        </FlexRow>
        {joinId !== undefined ? (
          <ScanCard css={[{ zIndex: 200 }, relative()]} joinId={joinId} />
        ) : (
          <StrokeText color={colors.danger}>Failed to get lobby code</StrokeText>
        )}
      </FlexColumn>
      <FlexColumn
        css={[absoluteFromDirections({ bottom: 0 }), padding('xl'), fullWidth]}
        center
      >
        <HosterStatusMessage />
      </FlexColumn>

      {appStore.lobbyGame && (
        <div
          css={[
            absolute(0, 0, 0, 0),
            { zIndex: 100, touchAction: 'none', pointerEvents: 'none' },
          ]}
        >
          <HosterGamePlayer noButtons lobbyGame={true} game={appStore.lobbyGame} />
        </div>
      )}
    </Splash>
  );
});
